import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PublicCanvasArtifact, PublicCanvasFile } from "./types";

/** What a read-only canvas surface can offer beyond viewing. */
export type PublicCanvasCapabilities = {
  /** Download on cards and notes. Off in the MCP widget, whose sandboxed iframe cannot download. */
  download: boolean;
  /** Copy link on cards. Off in the MCP widget, where the host denies the clipboard. */
  copyLink: boolean;
  /** Open on cards. Off in the MCP widget, where clicking a file card opens it and other cards have no pane. */
  openEntity: boolean;
};

const DEFAULT_CAPABILITIES: PublicCanvasCapabilities = {
  download: true,
  copyLink: true,
  openEntity: true,
};

export type PublicCanvasArtifactContextValue = {
  artifact: PublicCanvasArtifact;
  /** True when this tree is the public share SPA (no API / Liveblocks). */
  isPublicCanvasView: true;
  /** CDN origin the artifact's object keys resolve against. */
  shareOrigin: string;
  capabilities: PublicCanvasCapabilities;
  openFileId: string | null;
  openFile: PublicCanvasFile | null;
  openPublicFile: (fileRecordId: string) => void;
  closePublicFile: () => void;
};

const PublicCanvasArtifactContext =
  createContext<PublicCanvasArtifactContextValue | null>(null);

export function PublicCanvasArtifactProvider({
  artifact,
  shareOrigin,
  capabilities: capabilitiesOverride,
  children,
}: {
  artifact: PublicCanvasArtifact;
  shareOrigin: string;
  capabilities?: Partial<PublicCanvasCapabilities>;
  children: ReactNode;
}) {
  const { download, copyLink, openEntity } = {
    ...DEFAULT_CAPABILITIES,
    ...capabilitiesOverride,
  };
  const capabilities = useMemo(
    () => ({ download, copyLink, openEntity }),
    [download, copyLink, openEntity],
  );
  const [openFileId, setOpenFileId] = useState<string | null>(null);

  const openPublicFile = useCallback(
    (fileRecordId: string) => {
      if (!fileRecordId) return;
      if (!artifact.files[fileRecordId]) return;
      setOpenFileId(fileRecordId);
    },
    [artifact.files],
  );

  const closePublicFile = useCallback(() => {
    setOpenFileId(null);
  }, []);

  const openFile = openFileId ? (artifact.files[openFileId] ?? null) : null;

  const value = useMemo(
    (): PublicCanvasArtifactContextValue => ({
      artifact,
      isPublicCanvasView: true,
      shareOrigin,
      capabilities,
      openFileId,
      openFile,
      openPublicFile,
      closePublicFile,
    }),
    [
      artifact,
      shareOrigin,
      capabilities,
      openFileId,
      openFile,
      openPublicFile,
      closePublicFile,
    ],
  );

  return (
    <PublicCanvasArtifactContext.Provider value={value}>
      {children}
    </PublicCanvasArtifactContext.Provider>
  );
}

export function usePublicCanvasArtifact(): PublicCanvasArtifactContextValue {
  const ctx = useContext(PublicCanvasArtifactContext);
  if (!ctx) {
    throw new Error(
      "usePublicCanvasArtifact must be used within PublicCanvasArtifactProvider",
    );
  }
  return ctx;
}

/** Safe read for hooks that also run in the authenticated app. */
export function usePublicCanvasArtifactSafe(): PublicCanvasArtifactContextValue | null {
  return useContext(PublicCanvasArtifactContext);
}

export function useIsPublicCanvasView(): boolean {
  return useContext(PublicCanvasArtifactContext)?.isPublicCanvasView === true;
}

/** The read-only surface's capabilities; everything is on in the app. */
export function usePublicCanvasCapabilities(): PublicCanvasCapabilities {
  return (
    useContext(PublicCanvasArtifactContext)?.capabilities ??
    DEFAULT_CAPABILITIES
  );
}
